import React from 'react';
import { useQuery } from '@edreamsodigeo/frontend-api-client';

import { useSearchQuery } from '../SearchRequestProvider';
import { ITINERARY_SEARCH_QUERY } from '../operations/itinerarySearch';
import {
  useCreateMapiShoppingCart,
  useTrackErrors,
  useUpdateMeshTransactionId,
  useLoadingResultsPage,
  useSkipQueryMetric,
} from './hooks';
import FrontendAPIErrorManager from 'clientState/errorManagement';
import {
  ItinerarySearchQuery,
  SearchQueryQueryVariables,
  SearchRequest,
} from 'generated/graphql';
import { useEndSearchMetricEffect } from '../SearchTrackerProvider';
import { useFrontendApiBlockingError } from '@edreamsodigeo/frontend-core';
import { transformSuccessQuery } from '../transformSuccessQuery';
import { transformItinerarySearchResponse } from './transformItinerarySearchResponse';
import { Extensions } from 'clientState/resolvers/itinerary';
import { useAlternativeItineraryMutation } from 'clientState/providers/AlternativeItineraryMutationProvider';
import { useHash } from 'clientState/hooks/useUrlParams';
import { SearchResponseProviderProps } from './types';
import {
  SearchResponseContext,
  SearchResponseActionsContext,
  replaceItinerariesById,
} from './index';
import {
  useFrontendApiValidationError,
  VALIDATION_SEARCH_ERROR,
} from 'clientState/hooks/useFrontendApiValidationError/useFrontendApiValidationError';

const ItinerarySearchResponseProvider: React.FC<
  SearchResponseProviderProps
> = ({
  onNoSearchResults,
  onSearchParamsError,
  children,
  onCreateMapiShoppingCart,
  onSearchMapiError,
  onLoadingResultsPage,
}) => {
  const searchRequest = useSearchQuery();
  const hash = useHash();
  const {
    alternativeMutationLaunched,
    alternativeSearchId,
    alternativeItineraries,
  } = useAlternativeItineraryMutation();
  const isNotAllowedHash =
    hash && !(hash.includes('#results') || hash.includes('#/results'));
  const skipQuery =
    searchRequest === null ||
    alternativeMutationLaunched ||
    alternativeSearchId !== null ||
    !!isNotAllowedHash;
  useSkipQueryMetric(
    skipQuery,
    searchRequest,
    alternativeMutationLaunched,
    alternativeSearchId,
    isNotAllowedHash
  );
  const queryItinerarySearchResult = useQuery<
    ItinerarySearchQuery,
    SearchQueryQueryVariables
  >(ITINERARY_SEARCH_QUERY, {
    variables: {
      searchRequest: searchRequest as SearchRequest,
    },
    skip: skipQuery,
    notifyOnBackgroundFetching: true,
  });
  const { loading: loadingMapiShoppingCart } = useCreateMapiShoppingCart(
    onCreateMapiShoppingCart,
    onSearchMapiError,
    searchRequest,
    queryItinerarySearchResult.data
  );
  // Handles any broken flow error from the back-end, but not the validation
  // errors
  useFrontendApiBlockingError(queryItinerarySearchResult, [
    VALIDATION_SEARCH_ERROR,
  ]);
  // Handles the validation errors
  useFrontendApiValidationError(queryItinerarySearchResult);

  const itineraries =
    queryItinerarySearchResult.data?.itinerarySearch?.itineraries || [];
  const itinerariesWithAlternatives = replaceItinerariesById(
    itineraries,
    alternativeItineraries
  );

  if (queryItinerarySearchResult.data) {
    queryItinerarySearchResult.data.itinerarySearch.itineraries =
      itinerariesWithAlternatives;
  }

  const searchQueryState = transformSuccessQuery(
    {
      loading:
        queryItinerarySearchResult.loading ||
        queryItinerarySearchResult.fetching,
      error: queryItinerarySearchResult.errors,
      data: queryItinerarySearchResult.data,
      extensions: queryItinerarySearchResult.extensions,
    },
    transformItinerarySearchResponse,
    {}
  );
  const { loading: loadingSearch, data, error } = searchQueryState;

  const loading =
    loadingSearch ||
    loadingMapiShoppingCart ||
    searchRequest === null ||
    queryItinerarySearchResult.fetching ||
    (!error && queryItinerarySearchResult.data === null);
  const extensions = searchQueryState?.extensions;

  useEndSearchMetricEffect(queryItinerarySearchResult);
  useTrackErrors(data, loading, queryItinerarySearchResult.errors);
  useUpdateMeshTransactionId(data);
  useLoadingResultsPage(loading, onLoadingResultsPage);
  const nextQueryState = React.useMemo(
    () => ({
      loading,
      data: loading ? null : data,
      error,
      extensions: extensions as Extensions | null,
    }),
    [loading, data, error, extensions]
  );

  const actions = {
    onNoSearchResults,
    onSearchParamsError,
  };

  return (
    <>
      <FrontendAPIErrorManager
        errors={queryItinerarySearchResult.errors}
        id="SearchResponseProvider"
      />
      <SearchResponseContext.Provider value={nextQueryState}>
        <SearchResponseActionsContext.Provider value={actions}>
          {children}
        </SearchResponseActionsContext.Provider>
      </SearchResponseContext.Provider>
    </>
  );
};

export default ItinerarySearchResponseProvider;
